import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Sidebar from "@/components/inbox/Sidebar";
import ThreadList from "@/components/inbox/ThreadList";
import SentList from "@/components/inbox/SentList";
import ThreadDetail from "@/components/inbox/ThreadDetail";
import ComposeDialog from "@/components/inbox/ComposeDialog";
import TagManagerDialog from "@/components/inbox/TagManagerDialog";
import PipelineManagerDialog from "@/components/inbox/PipelineManagerDialog";
import MobileTabBar from "@/components/inbox/MobileTabBar";
import AddPhonePrompt from "@/components/AddPhonePrompt";
import { useSyncStatus } from "@/components/inbox/SyncProgress";
import { toast } from "sonner";

export default function Inbox() {
  const { user, refresh } = useAuth();
  const [filter, setFilter] = useState("all");
  const [status, setStatus] = useState(null);
  const [activeMyTag, setActiveMyTag] = useState(null);
  const [activeTeamTag, setActiveTeamTag] = useState(null);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [activeFolder, setActiveFolder] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [myTags, setMyTags] = useState([]);
  const [teamTags, setTeamTags] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [folders, setFolders] = useState([]);
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [composeOpen, setComposeOpen] = useState(false);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [phonePromptOpen, setPhonePromptOpen] = useState(false);
  const syncInboxes = useSyncStatus();

  // Auto-prompt: if the logged-in user has no phone on file, show the
  // "add phone for faster sign-in" modal. Suppressed for 7 days after the
  // user clicks "Not now" so we don't badger them every visit.
  useEffect(() => {
    if (!user) return;
    if (user.phone) return;
    let skippedAt = 0;
    try {
      skippedAt = parseInt(localStorage.getItem("ibx_phone_prompt_skipped_at") || "0", 10);
    } catch {
      // localStorage may be unavailable (private mode); treat as never-skipped.
    }
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    if (skippedAt && Date.now() - skippedAt < SEVEN_DAYS) return;
    // Small delay so the inbox UI is visible behind the modal first.
    const t = setTimeout(() => setPhonePromptOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  // Search runs on Enter / Search button; clearing the box resets immediately.
  useEffect(() => {
    if (!search.trim()) setDebouncedSearch("");
  }, [search]);
  const [searchNonce, setSearchNonce] = useState(0);
  const submitSearch = useCallback((value) => {
    const v = (value ?? search).trim();
    if (value !== undefined) setSearch(value);
    setDebouncedSearch(v);
    setSearchNonce((n) => n + 1);
  }, [search]);

  // Prev/next navigation across the currently loaded thread list.
  // Position + handlers are computed from `threads` + `selectedId` and passed
  // down to ThreadDetail so the arrows in the reader header can jump between
  // conversations without going back to the list.
  const currentIndex = selectedId
    ? threads.findIndex((t) => t.id === selectedId)
    : -1;
  const goPrev = currentIndex > 0
    ? () => setSelectedId(threads[currentIndex - 1].id)
    : null;
  const goNext = currentIndex >= 0 && currentIndex < threads.length - 1
    ? () => setSelectedId(threads[currentIndex + 1].id)
    : null;

  // Keyboard nav — J/K to move, Esc to close. Ignored while typing in inputs.
  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape") { setSelectedId(null); e.preventDefault(); return; }
      if (e.key === "j" || e.key === "J") { goNext?.(); e.preventDefault(); return; }
      if (e.key === "k" || e.key === "K") { goPrev?.(); e.preventDefault(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, goPrev, goNext]);

  const loadSeq = useRef(0);
  const loadThreads = useCallback(async ({ silent = false } = {}) => {
    const seq = ++loadSeq.current;
    if (!silent) setLoading(true);
    try {
      const params = { limit: 100 };
      if (debouncedSearch) params.q = debouncedSearch;
      if (filter && filter !== "all") params.filter = filter;
      if (status) params.status = status;
      if (activeMyTag) params.my_tag = activeMyTag;
      if (activeTeamTag) params.team_tag = activeTeamTag;
      if (activeCustomer) params.customer_domain = activeCustomer;
      if (activeFolder) params.folder_id = activeFolder;
      const { data } = await api.get("/threads", { params, ...(debouncedSearch ? { timeout: 45000 } : {}) });
      if (seq !== loadSeq.current) return;
      setThreads(data.threads);
      setTotal(data.total);
    } catch (e) {
      if (seq === loadSeq.current && debouncedSearch) toast.error(e?.code === "ECONNABORTED" ? "Search is taking too long — try fewer or more specific words" : "Search failed — try again");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [debouncedSearch, searchNonce, filter, status, activeMyTag, activeTeamTag, activeCustomer, activeFolder]);

  const loadCounts = useCallback(async (statusList) => {
    try {
      const requests = [
        api.get("/threads", { params: { limit: 1 } }),
        api.get("/threads", { params: { limit: 1, filter: "unread" } }),
        api.get("/threads", { params: { limit: 1, filter: "assigned_to_me" } }),
        api.get("/threads", { params: { limit: 1, filter: "unassigned" } }),
        ...(statusList || []).map((s) => api.get("/threads", { params: { limit: 1, status: s.id } })),
      ];
      const results = await Promise.all(requests);
      const next = {
        all: results[0].data.total,
        unread: results[1].data.total,
        assigned_to_me: results[2].data.total,
        unassigned: results[3].data.total,
      };
      (statusList || []).forEach((s, i) => {
        next[`status_${s.id}`] = results[4 + i].data.total;
      });
      setCounts(next);
    } catch (e) { void e; }
  }, []);

  const loadSidebar = useCallback(async () => {
    try {
      const [st, u, mt, tt, c, f] = await Promise.all([
        api.get("/pipeline-statuses"),
        api.get("/users"),
        api.get("/my-tags"),
        api.get("/team-tags"),
        api.get("/customers"),
        api.get("/folders"),
      ]);
      setStatuses(st.data);
      setUsers(u.data);
      setMyTags(mt.data);
      setTeamTags(tt.data);
      setCustomers(c.data);
      setFolders(f.data);
      // Counts depend on the loaded statuses
      loadCounts(st.data);
    } catch (e) { void e; }
  }, [loadCounts]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadSidebar(); }, [loadSidebar]);

  // Keep tab title in sync with unread count so it's useful when the phone is locked.
  useEffect(() => {
    const unread = counts?.unread || 0;
    document.title = unread > 0 ? `(${unread}) Internal Inbox` : "Internal Inbox";
    return () => { document.title = "Internal Inbox"; };
  }, [counts?.unread]);

  // Auto-poll the thread list every 10s for near-realtime updates
  // (server-side Gmail sync runs every 30s; this just re-reads the mirror.)
  useEffect(() => {
    const id = setInterval(() => { loadThreads({ silent: true }); }, 10000);
    return () => clearInterval(id);
  }, [loadThreads]);

  // Manual "Refresh now": kicks Gmail sync (fire-and-forget on the backend
  // so Cloudflare's 100s edge timeout can't kill a long sync) and reloads
  // the local thread list. New messages appear as the background job writes
  // them to Mongo — the existing 10s poller will surface them.
  const refreshNow = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/inboxes/sync-all");
      setLastSyncedAt(data.started_at);
      const queued = (data.queued || []).length;
      const running = (data.already_running || []).length;
      if (queued > 0) {
        toast.success(`Syncing ${queued} inbox${queued === 1 ? "" : "es"} in the background — new mail will appear shortly.`);
      } else if (running > 0) {
        toast.info("Sync is already running — new mail will appear as it comes in.");
      } else {
        toast.info("No connected Gmail inboxes — connect one in Admin.");
      }
      await Promise.all([loadThreads(), loadSidebar()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [loadThreads, loadSidebar]);

  const onCreateFolder = async () => {
    const name = window.prompt("Folder name:");
    if (!name || !name.trim()) return;
    try {
      await api.post("/folders", { name: name.trim() });
      toast.success(`Folder "${name}" created`);
      loadSidebar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create folder");
    }
  };

  const onDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Threads inside won't be deleted.`)) return;
    try {
      await api.delete(`/folders/${folder.id}`);
      toast.success("Folder deleted");
      if (activeFolder === folder.id) setActiveFolder(null);
      loadSidebar();
    } catch (e) {
      toast.error("Couldn't delete folder");
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white" data-testid="inbox-page">
      {/* Mobile drawer overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-zinc-900/40 z-30"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      <div
        className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:relative inset-y-0 left-0 z-40 transition-transform duration-200 md:transition-none`}
        data-testid="sidebar-wrapper"
      >
        <Sidebar
          filter={filter} setFilter={(v) => { setFilter(v); setSidebarOpen(false); }}
          statuses={statuses} status={status} setStatus={(v) => { setStatus(v); setSidebarOpen(false); }}
          myTags={myTags} activeMyTag={activeMyTag} setActiveMyTag={(v) => { setActiveMyTag(v); setSidebarOpen(false); }}
          teamTags={teamTags} activeTeamTag={activeTeamTag} setActiveTeamTag={(v) => { setActiveTeamTag(v); setSidebarOpen(false); }}
          customers={customers} activeCustomer={activeCustomer} setActiveCustomer={(v) => { setActiveCustomer(v); setSidebarOpen(false); }}
          folders={folders} activeFolder={activeFolder} setActiveFolder={(v) => { setActiveFolder(v); setSidebarOpen(false); }}
          onCreateFolder={onCreateFolder} onDeleteFolder={onDeleteFolder}
          onCompose={() => { setComposeOpen(true); setSidebarOpen(false); }}
          onManageTags={() => { setTagManagerOpen(true); setSidebarOpen(false); }}
          onManagePipeline={() => { setPipelineOpen(true); setSidebarOpen(false); }}
          onCloseDrawer={() => setSidebarOpen(false)}
          counts={counts}
        />
      </div>
      {/* Thread list — hidden entirely whenever a thread is open so the reader
          takes the full remaining page width. The list re-appears when the user
          hits the back button in the thread header (setSelectedId(null)). */}
      <div
        className={`${selectedId ? "hidden" : "flex flex-1"} min-w-0`}
        data-testid="thread-list-wrapper"
      >
        {filter === "sent" ? (
          <SentList
            selectedId={selectedId}
            onSelect={setSelectedId}
            onOpenSidebar={() => setSidebarOpen(true)}
            compact={!!selectedId}
          />
        ) : (
          <ThreadList
            threads={threads}
            loading={loading}
            total={total}
            selectedId={selectedId}
            onSelect={setSelectedId}
            search={search}
            setSearch={setSearch}
            onSearchSubmit={submitSearch}
            activeQuery={debouncedSearch}
            onClearSearch={() => submitSearch("")}
            statuses={statuses}
            status={status}
            setStatus={setStatus}
            onRefresh={refreshNow}
            refreshing={refreshing}
            lastSyncedAt={lastSyncedAt}
            compact={!!selectedId}
            onOpenSidebar={() => setSidebarOpen(true)}
            onLocalChange={() => { loadThreads(); loadSidebar(); }}
            syncInboxes={syncInboxes}
          />
        )}
      </div>
      {/* Thread detail — fills the remaining space when a thread is selected. */}
      {selectedId && (
        <div className="flex-1 flex min-w-0" data-testid="thread-detail-wrapper">
          <ThreadDetail
            threadId={selectedId}
            currentUser={user}
            users={users}
            folders={folders}
            statuses={statuses}
            myTagsList={myTags}
            teamTagsList={teamTags}
            onClose={() => setSelectedId(null)}
            onPrev={goPrev}
            onNext={goNext}
            position={currentIndex >= 0 ? { index: currentIndex + 1, total: threads.length } : null}
            onChange={() => { loadThreads(); loadSidebar(); }}
            onMyTagsChange={() => { loadSidebar(); loadThreads(); }}
            onTeamTagsChange={() => { loadSidebar(); loadThreads(); }}
          />
        </div>
      )}

      <ComposeDialog open={composeOpen} onOpenChange={setComposeOpen} onSent={async () => { await refreshNow(); }} />
      <TagManagerDialog open={tagManagerOpen} onOpenChange={setTagManagerOpen} onChanged={() => { loadSidebar(); loadThreads(); }} />
      <PipelineManagerDialog
        open={pipelineOpen}
        onOpenChange={setPipelineOpen}
        canEdit={user && user.role === "owner"}
        onChanged={() => { loadSidebar(); loadThreads(); }}
      />

      {/* Hide the mobile tab bar while a thread is open so the Composer has
          the entire bottom of the viewport. Tap "back" inside the thread to
          return to the inbox + tab bar. */}
      {!selectedId && (
        <MobileTabBar
          onInboxTab={() => {
            setSelectedId(null);
            setFilter("all"); setStatus(null);
            setActiveMyTag(null); setActiveTeamTag(null);
            setActiveCustomer(null); setActiveFolder(null);
            setSidebarOpen(false);
            window.scrollTo({ top: 0 });
          }}
          onSearchTab={() => {
            setSelectedId(null);
            setSidebarOpen(false);
            // Focus the search input
            setTimeout(() => {
              const el = document.querySelector('[data-testid="thread-search-input"]');
              if (el) { el.focus(); el.scrollIntoView({ block: "start" }); }
            }, 60);
          }}
          onMenuTab={() => setSidebarOpen((v) => !v)}
        />
      )}

      <AddPhonePrompt
        open={phonePromptOpen}
        onOpenChange={setPhonePromptOpen}
        onSaved={refresh}
      />
    </div>
  );
}
